import { useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Send, Calculator, MessageSquare } from 'lucide-react';
import {
  CalcApi,
  PreviewApi,
  PricingApi,
  satangToBaht,
  type Brand,
  type CustomerType,
  type CalcResult,
  type PreviewResult,
  type PreviewContext,
  type PreviewConversation,
  type PreviewAi,
  type PreviewAiModel,
  type AssistantDraft,
} from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

export default function PreviewPage() {
  const [tab, setTab] = useState<'reply' | 'calc'>('reply');
  return (
    <div className="space-y-4">
      <div className="flex gap-1 bg-muted rounded-lg p-1 w-fit">
        <button
          onClick={() => setTab('reply')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
            tab === 'reply' ? 'bg-background shadow-sm' : 'text-muted-foreground'
          }`}
        >
          <MessageSquare size={14} className="inline mr-1" /> ทดสอบ Bot Reply
        </button>
        <button
          onClick={() => setTab('calc')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
            tab === 'calc' ? 'bg-background shadow-sm' : 'text-muted-foreground'
          }`}
        >
          <Calculator size={14} className="inline mr-1" /> คำนวณราคา (Pricing Engine)
        </button>
      </div>

      {tab === 'reply' ? <ReplyTester /> : <CalcTester />}
      {tab === 'reply' && <TestPageDrafts />}
    </div>
  );
}

// ============================================================
// Reply tester — chat-style (Q ขวา/A ซ้าย) + history + preset scenarios
// ============================================================
type ChatTurn = {
  id: string;
  q: string;
  reply: string | null;
  reason: string;
  imageUrl?: string | null | undefined;
  score?: number | null | undefined;
  sourceQ?: string | undefined;
  sourcePage?: string | undefined;
  ts: number;
  loading?: boolean | undefined;
  error?: string | undefined;
  conversation?: PreviewConversation | undefined;
  ai?: PreviewAi | null | undefined;
};

const PRESET_SCENARIOS = [
  { label: '👋 ทักทาย', text: 'สวัสดีค่ะ' },
  { label: '💰 ถามราคา', text: 'ราคาเท่าไหร่คะ' },
  { label: '🛒 รับ 1 ชุด', text: 'รับ 1 ชุด สีดำ ไซส์ M ค่ะ' },
  { label: '🛒 รับ 2 ชุด', text: 'รับ 2 ชุด สีดำ ไซส์ M ค่ะ' },
  { label: '🛒 รับ 3 ชุด', text: 'รับ 3 ชุด ค่ะ' },
  { label: '🎨 มีสีเขียวไหม', text: 'มีสีเขียวไหมคะ' },
  { label: '📏 ขอตารางไซส์', text: 'ขอตารางไซส์หน่อยค่ะ' },
  { label: '📸 ขอดูรูป', text: 'ขอดูรูปหน่อยค่ะ' },
  { label: '🚚 ค่าส่งเท่าไหร่', text: 'ค่าส่งเท่าไหร่คะ' },
  { label: '🙏 ขอบคุณ', text: 'ขอบคุณค่ะ' },
];

// คุยต่อเนื่อง — กดตามลำดับเพื่อดูการจำบริบท/ถามทวน (ใช้กับเพจที่ตั้ง "ช่องข้อมูลสั่งซื้อ" ไว้ เช่น Cher Scrub)
const CONVERSATION_SCENARIOS = [
  { label: '1️⃣ บอกสินค้า+สี', text: 'อยากได้ชุดสีดำค่ะ' },
  { label: '2️⃣ ตอบไซซ์สั้น ๆ', text: 'M ค่ะ' },
  { label: '3️⃣ ตอบจำนวน', text: '2 ชุด' },
  { label: '🔁 เปลี่ยนใจ', text: 'เปลี่ยนเป็นสีแดงค่ะ' },
  { label: '❓ คำกำกวม', text: 'เอาสีเทา 1 ชุด ไซซ์ L' },
  { label: '📦 สีที่หมด', text: 'รับชุดสีม่วง M 1 ชุด' },
  { label: '🪡 ตอบเรื่องปัก', text: 'ปักชื่อ Dr. Test' },
  { label: '✅ ยืนยัน', text: 'ยืนยันค่ะ' },
  { label: '📦 ข้อมูลจัดส่ง (ทดสอบ)', text: 'ผู้รับทดสอบ 99/9 ถนนทดสอบ กรุงเทพ 10500 โทร 0800000000' },
];

function ReplyTester() {
  const [text, setText] = useState('สวัสดีค่ะ ราคาเท่าไหร่');
  const [customerType, setCustomerType] = useState<'new' | 'returning'>('new');
  const [pageId, setPageId] = useState<string>('');
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  // บริบทจาก server รอบก่อน — ส่งกลับตามเดิม server ตรวจซ้ำกับฐานจริงทุกครั้ง; เปลี่ยนเพจ/ประเภทลูกค้า/ล้าง = เริ่มใหม่
  const contextRef = useRef<PreviewContext | null>(null);
  // AI คิดเฉพาะข้อความที่ระบบกฎไม่เข้าใจ (Workers AI ในบัญชีเดิม ข้อมูลไม่ออกนอกระบบ)
  const [aiModel, setAiModel] = useState<PreviewAiModel | ''>('');
  const resetConversation = () => { contextRef.current = null; setTurns([]); };
  const latest = [...turns].reverse().find((t) => t.conversation && !t.loading && !t.error)?.conversation;

  async function send(q: string) {
    if (sendingRef.current || !q.trim()) return;
    sendingRef.current = true;
    setSending(true);
    const id = String(Date.now() + Math.random());
    setTurns((prev) => [
      ...prev,
      { id, q, reply: null, reason: '', ts: Date.now(), loading: true },
    ]);
    try {
      const payload: Parameters<typeof PreviewApi.run>[0] = { text: q, previewCustomerType: customerType, context: contextRef.current };
      if (aiModel) {
        const history = turns.filter((t) => t.reply && !t.error && !t.loading).slice(-6).map((t) => ({ q: t.q.slice(0, 500), a: (t.reply ?? '').slice(0, 1500) }));
        payload.ai = { model: aiModel, history };
      }
      if (pageId) payload.pageId = pageId;
      const res = await PreviewApi.run(payload);
      contextRef.current = res.context ?? null;
      setTurns((prev) =>
        prev.map((t) =>
          t.id === id
            ? {
                ...t,
                reply: res.reply ?? null,
                reason: res.reason,
                imageUrl: res.imageUrl ?? null,
                score: res.score,
                sourceQ: res.sourceQ,
                sourcePage: res.sourcePage,
                conversation: res.conversation,
                ai: res.ai,
                loading: false,
              }
            : t,
        ),
      );
    } catch (e) {
      setTurns((prev) =>
        prev.map((t) =>
          t.id === id
            ? { ...t, loading: false, error: e instanceof Error ? e.message : String(e) }
            : t,
        ),
      );
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  }

  function handleSend() {
    if (sendingRef.current || !text.trim()) return;
    void send(text.trim());
    setText('');
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* LEFT: Preset scenarios + settings */}
      <div className="space-y-3 border border-border rounded-lg p-3 lg:col-span-1">
        <h3 className="font-semibold text-sm flex items-center gap-1">
          ⚡ คลิกเคสตัวอย่าง
        </h3>
        <p className="text-xs text-muted-foreground">
          คลิก → ส่งเป็นข้อความถัดไปในบทสนทนาเดียวกัน บอทจำข้อมูลที่ลูกค้าให้แล้ว (สินค้า/สี/ไซซ์/จำนวน) ไม่ถามซ้ำ
        </p>
        <div className="grid grid-cols-1 gap-1.5">
          {PRESET_SCENARIOS.map((s) => (
            <Button
              key={s.text}
              size="sm"
              variant="outline"
              className="justify-start h-8 text-xs"
              disabled={sending}
              onClick={() => send(s.text)}
            >
              {s.label}
              <span className="text-muted-foreground ml-2 truncate">— "{s.text}"</span>
            </Button>
          ))}
        </div>

        <h3 className="font-semibold text-sm pt-2 border-t border-border">💬 คุยต่อเนื่อง (กดตามลำดับ)</h3>
        <div className="grid grid-cols-1 gap-1.5">
          {CONVERSATION_SCENARIOS.map((s) => (
            <Button
              key={s.text}
              size="sm"
              variant="outline"
              className="justify-start h-8 text-xs"
              disabled={sending}
              onClick={() => send(s.text)}
            >
              {s.label}
              <span className="text-muted-foreground ml-2 truncate">— "{s.text}"</span>
            </Button>
          ))}
        </div>

        <div className="pt-2 border-t border-border space-y-1">
          <label className="text-xs font-semibold">🧠 ให้ AI คิดเมื่อระบบกฎไม่เข้าใจ</label>
          <select
            className="w-full h-8 text-xs border border-border rounded-md bg-background px-2"
            value={aiModel}
            disabled={sending}
            onChange={(e) => setAiModel(e.target.value as PreviewAiModel | '')}
          >
            <option value="">ปิด — ใช้ระบบกฎอย่างเดียว</option>
            <option value="qwen3-30b">เปิด — Qwen3 30B (ประหยัด neurons)</option>
            <option value="gpt-oss-20b">เปิด — gpt-oss 20B</option>
          </select>
          <p className="text-[10px] text-muted-foreground">
            ใช้ Workers AI ในบัญชี Cloudflare เดิม ข้อมูลไม่ส่งออกนอกระบบ · AI คิดและเลือกการกระทำ ข้อความที่บอตตอบมาจาก FAQ/Reply Templates ในฐานเท่านั้น · ข้อความที่ AI แต่งเองแสดงเป็น "ร่าง" ไม่ส่งลูกค้า · บุคลิกแก้ได้ที่ Reply Templates key <code>ai_persona</code>
          </p>
        </div>

        <MemoryPanel conversation={latest} />

        <div className="pt-2 border-t border-border space-y-2">
          <div>
            <label className="text-xs text-muted-foreground">ทดสอบในฐานะ:</label>
            <div className="flex gap-1 mt-1">
              <Button
                size="sm"
                variant={customerType === 'new' ? 'default' : 'outline'}
                className="flex-1 h-8 text-xs"
                disabled={sending}
                onClick={() => { if (sendingRef.current) return; setCustomerType('new'); resetConversation(); }}
              >
                🆕 ลูกค้าใหม่
              </Button>
              <Button
                size="sm"
                variant={customerType === 'returning' ? 'default' : 'outline'}
                className="flex-1 h-8 text-xs"
                disabled={sending}
                onClick={() => { if (sendingRef.current) return; setCustomerType('returning'); resetConversation(); }}
              >
                🔁 ลูกค้าเก่า
              </Button>
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">
              pageId (เว้นว่าง = It's New Me sandbox)
            </label>
            <Input
              placeholder="674105776054367"
              value={pageId}
              disabled={sending}
              onChange={(e) => { if (sendingRef.current) return; setPageId(e.target.value); resetConversation(); }}
              className="h-8 text-xs"
            />
          </div>
          {turns.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="w-full h-8 text-xs"
              disabled={sending}
              onClick={() => { if (!sendingRef.current) resetConversation(); }}
            >
              🗑 ล้างประวัติแชทและบริบท ({turns.length} รายการ)
            </Button>
          )}
        </div>
      </div>

      {/* RIGHT: Chat history (Q+A bubbles) */}
      <div className="border border-border rounded-lg p-3 space-y-3 lg:col-span-2 bg-muted/10 flex flex-col">
        <h3 className="font-semibold text-sm">💬 ประวัติคำถาม-คำตอบ</h3>
        <p className="text-xs text-muted-foreground">
          ใช้ FAQ ราคา โปร สี ไซซ์ และสต๊อกที่ตั้งค่าจริง ไม่ค้น KB ด้วยความหมาย · เรียก AI เฉพาะเมื่อเลือกโมเดลด้านซ้ายและระบบกฎตอบไม่ได้ (Workers AI ในบัญชีเดิม) ไม่บันทึกลูกค้า ออเดอร์ หรือสถิติการใช้ KB และไม่ส่งข้อความไป Messenger
          ทดสอบได้แม้ปิดตอบอัตโนมัติ โดยไม่เปลี่ยนสวิตช์ของเพจ · คุยต่อเนื่องได้กับเพจที่ตั้ง “ช่องข้อมูลสั่งซื้อ” (ตั้งที่ Order Builder) · คำถาม/ข้อความของบอทแก้ได้ที่คำตอบ → Reply Templates
        </p>
        <div className="flex-1 min-h-[400px] max-h-[600px] overflow-y-auto space-y-3 p-1">
          {turns.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-8">
              ยังไม่มีประวัติ — คลิกเคสตัวอย่างซ้ายมือ หรือพิมพ์ข้อความใต้ตรงนี้
            </div>
          )}
          {turns.map((t, i) => (
            <ChatBubble key={t.id} turn={t} isLast={i === turns.length - 1} disabled={sending} onPick={(v) => send(v)} />
          ))}
        </div>

        {/* Input box */}
        <div className="flex gap-2 pt-2 border-t border-border">
          <Input
            placeholder="พิมพ์ข้อความที่ลูกค้าจะส่ง... (Enter เพื่อส่ง)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button onClick={handleSend} disabled={sending || !text.trim()}>
            <Send size={14} className="mr-1" /> ส่ง
          </Button>
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ turn, isLast, disabled, onPick }: { turn: ChatTurn; isLast: boolean; disabled: boolean; onPick: (value: string) => void }) {
  const conv = turn.conversation;
  return (
    <div className="space-y-2">
      {/* Q — ลูกค้า (ขวา) */}
      <div className="flex justify-end">
        <div className="max-w-[75%] bg-blue-500 text-white rounded-2xl rounded-br-sm px-3 py-2 text-sm shadow">
          {turn.q}
        </div>
      </div>

      {/* A — บอต (ซ้าย) */}
      <div className="flex justify-start">
        <div className="max-w-[85%] space-y-1">
          {turn.loading ? (
            <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-muted-foreground italic">
              บอตกำลังคิด…
            </div>
          ) : turn.error ? (
            <div className="bg-red-50 border border-red-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-red-700">
              ❌ {turn.error}
            </div>
          ) : turn.reply ? (
            <>
              <div className="bg-white border border-border rounded-2xl rounded-bl-sm px-3 py-2 text-sm whitespace-pre-wrap shadow-sm">
                {turn.reply}
              </div>
              {turn.imageUrl && (
                <img src={turn.imageUrl} alt="รูปที่บอทส่ง" className="mt-1 max-w-[220px] rounded-lg border shadow-sm" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />
              )}
              <div className="flex gap-1.5 flex-wrap text-[10px] pl-2">
                {turn.ai?.status !== 'answered' && (
                  <Badge variant="outline" className="text-[10px] py-0">
                    {reasonLabel(turn.reason)}
                  </Badge>
                )}
                {turn.score != null && (
                  <Badge variant="outline" className="text-[10px] py-0">
                    similarity: {turn.score.toFixed(2)}
                  </Badge>
                )}
                {turn.sourceQ && (
                  <span className="text-muted-foreground italic">
                    จาก KB: "{turn.sourceQ.slice(0, 60)}..."
                  </span>
                )}
                {!!turn.ai?.faqQuestions?.length && turn.ai.status === 'answered' && (
                  <Badge variant="outline" className="text-[10px] py-0 border-violet-300 text-violet-700">
                    FAQ ที่เลือก: {turn.ai.faqQuestions.join(' · ').slice(0, 80)}
                  </Badge>
                )}
                {((conv && (conv.status === 'awaiting_admin' || conv.status === 'handoff' || conv.status === 'draft_order')) || (turn.ai?.status === 'answered' && turn.ai.action === 'handoff')) && (
                  <Badge variant="outline" className="text-[10px] py-0 border-amber-300 text-amber-700">
                    🧪 Preview: ไม่ได้สร้างออเดอร์ · ไม่ได้ส่งต่อแอดมินจริง
                  </Badge>
                )}
                {turn.ai && (
                  <Badge variant="outline" className={`text-[10px] py-0 ${turn.ai.status === 'answered' ? 'border-violet-300 text-violet-700' : 'border-amber-300 text-amber-700'}`}>
                    🧠 {turn.ai.model} · {aiStatusLabel(turn.ai)} · {turn.ai.usage.neurons} neurons (วันนี้ {turn.ai.usage.todayNeurons}/{turn.ai.usage.cap}) · {(turn.ai.latencyMs / 1000).toFixed(1)} วิ
                  </Badge>
                )}
                {turn.ai?.needsAdmin && turn.ai.status === 'answered' && (
                  <Badge variant="outline" className="text-[10px] py-0 border-amber-300 text-amber-700">🙋 AI แนะนำให้แอดมินดูต่อ</Badge>
                )}
                {conv?.contextReset && (
                  <Badge variant="outline" className="text-[10px] py-0 border-amber-300 text-amber-700">
                    เริ่มบริบทใหม่ ({conv.contextReset === 'scope_mismatch' ? 'เปลี่ยนเพจ/แบรนด์' : 'ยาวเกินกำหนด'})
                  </Badge>
                )}
              </div>
              {turn.ai?.draft && (
                <div className="ml-2 text-xs rounded border border-violet-200 bg-violet-50 px-2 py-1 whitespace-pre-wrap">
                  <div className="font-medium text-violet-700">📝 ร่างของ AI — ไม่ส่งลูกค้า ยังไม่ได้ตรวจข้อเท็จจริง</div>
                  {turn.ai.draft}
                  {!!turn.ai.draftFlags?.length && <div className="text-amber-700 mt-0.5">⚠ พบคำเสี่ยง: {turn.ai.draftFlags.join(', ')}</div>}
                </div>
              )}
              {conv && (conv.captured.length > 0 || conv.remembered.length > 0) && (
                <div className="flex gap-1 flex-wrap text-[10px] pl-2">
                  {conv.captured.map((c) => (
                    <span key={`c-${c}`} className="rounded bg-green-50 border border-green-200 text-green-700 px-1.5">ได้จากข้อความนี้: {c}</span>
                  ))}
                  {conv.remembered.map((c) => (
                    <span key={`r-${c}`} className="rounded bg-slate-50 border border-slate-200 text-slate-600 px-1.5">จำจากก่อนหน้า: {c}</span>
                  ))}
                </div>
              )}
              {isLast && conv && conv.options.length > 0 && (
                <div className="flex gap-1.5 flex-wrap pl-2 pt-1">
                  {conv.options.map((o) => (
                    <Button key={o.value} size="sm" variant="outline" className="h-7 text-xs" disabled={disabled} onClick={() => onPick(o.value)}>
                      {o.label}
                    </Button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-amber-700 italic">
              (บอตไม่ตอบ — page log-only หรือ reason: {turn.reason})
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function aiStatusLabel(ai: PreviewAi): string {
  switch (ai.status) {
    case 'answered': return ({ faq: 'AI เลือกคำตอบ FAQ', ask_order: 'AI เลือกชวนสั่ง', greet: 'AI เลือกทักทาย', handoff: 'AI เลือกส่งต่อแอดมิน' } as const)[ai.action ?? 'handoff'];
    case 'rejected': return `ปัดคำตอบ AI (${ai.reason ?? ''}) ใช้คำตอบระบบเดิม`;
    case 'over_budget': return 'ครบเพดาน neurons วันนี้ — ไม่เรียก AI';
    default: return 'AI ขัดข้อง — ใช้คำตอบระบบเดิม';
  }
}

function reasonLabel(r: string): string {
  switch (r) {
    case 'promo': return '💰 ตอบจาก promo';
    case 'order': return '🛒 ตอบจาก order calc';
    case 'faq': return '❓ ตอบจาก FAQ';
    case 'size': return '📏 แนะนำไซซ์';
    case 'color': return '🎨 ตอบเรื่องสี';
    case 'stock': return '📦 เช็คสต๊อก สี×ไซซ์';
    case 'kb': return '📚 ตอบจาก KB';
    case 'greeting': return '👋 ตอบทักทาย';
    case 'fallback': return '⚠ fallback (ไม่พบ)';
    case 'empty': return '(ข้อความว่าง)';
    case 'log_only': return '📵 page log-only';
    case 'conversation_close': return '🛍️ ขั้นปิดการขาย';
    case 'conversation_ask': return '🧵 ถามข้อมูลที่ยังขาด';
    case 'conversation_clarify': return '❓ ถามทวน (กำกวม)';
    case 'conversation_stock': return '📦 สต๊อกไม่พอ — เสนอทางเลือก';
    case 'conversation_quote': return '🧾 สรุปยอดจาก Pricing Engine';
    case 'conversation_unsaved': return '⏸ ยืนยันแล้ว แต่ยังไม่บันทึกออเดอร์';
    case 'conversation_handoff': return '🙋 ควรส่งต่อแอดมิน';
    case 'conversation_answer': return '💬 ตอบจากระบบเดิม (FAQ/สี/โปร)';
    default: return r;
  }
}

const STATUS_LABEL: Record<PreviewConversation['status'], string> = {
  collecting: 'กำลังเก็บข้อมูล',
  clarifying: 'รอลูกค้าตอบคำถามทวน',
  out_of_stock: 'รอเลือกตัวเลือกที่มีของ',
  quoted: 'สรุปยอดแล้ว กำลังถามเรื่องปัก',
  confirming: 'รอลูกค้ายืนยันรายการ',
  payment: 'รอสลิปและข้อมูลจัดส่ง',
  draft_order: 'มีร่างออเดอร์ รอแอดมินตรวจ (ยังไม่บันทึก)',
  awaiting_admin: 'ลูกค้ายืนยันแล้ว — รอแอดมิน (ยังไม่บันทึกออเดอร์)',
  handoff: 'ควรให้แอดมินดูต่อ',
};

/** สิ่งที่บอทจำไว้ในบทสนทนานี้ — แสดงตามที่ server ตรวจกับฐานแล้ว */
function MemoryPanel({ conversation }: { conversation: PreviewConversation | undefined }) {
  return (
    <div className="pt-2 border-t border-border space-y-1.5">
      <h3 className="font-semibold text-sm">🧠 ข้อมูลที่บอทจำไว้</h3>
      {!conversation || conversation.items.length === 0 ? (
        <p className="text-xs text-muted-foreground">ยังไม่มีรายการ — เริ่มคุยเรื่องสั่งซื้อแล้วจะแสดงที่นี่</p>
      ) : (
        <>
          <p className="text-xs">สถานะ: <span className="font-medium">{STATUS_LABEL[conversation.status]}</span></p>
          {conversation.items.map((it, i) => (
            <div key={i} className="text-xs rounded border border-border bg-background p-2 space-y-0.5">
              <div className="font-medium">รายการที่ {i + 1}</div>
              <div>สินค้า: {it.product ?? <span className="text-amber-700">ยังไม่ระบุ</span>}</div>
              <div>สี: {it.color ?? <span className="text-amber-700">ยังไม่ระบุ</span>}</div>
              <div>ไซซ์: {it.size ?? <span className="text-amber-700">ยังไม่ระบุ</span>}</div>
              <div>จำนวน: {it.qty ?? <span className="text-amber-700">ยังไม่ระบุ</span>}</div>
            </div>
          ))}
          {conversation.quote && (
            <div className="text-xs rounded border border-green-200 bg-green-50 p-2">
              ยอดจาก Pricing Engine: <span className="font-semibold">{satangToBaht(conversation.quote.total)} ฿</span>
              {conversation.quote.discount > 0 && <> (ลด {satangToBaht(conversation.quote.discount)} ฿)</>}
              {conversation.quote.promo && <div>โปร: {conversation.quote.promo}</div>}
            </div>
          )}
          {conversation.draftOrder && (
            <div className="text-xs rounded border border-amber-300 bg-amber-50 p-2 whitespace-pre-wrap">
              <div className="font-medium">🧾 ร่างออเดอร์ (ยังไม่บันทึก)</div>
              <div>ปัก: {conversation.draftOrder.embroidery ?? 'ไม่ปัก'}</div>
              <div>จัดส่ง: {conversation.draftOrder.shipping ?? '-'}</div>
            </div>
          )}
          <p className="text-[10px] text-muted-foreground">Preview ไม่สร้างออเดอร์ ไม่บันทึกลูกค้า และไม่ส่งข้อความจริง</p>
        </>
      )}
    </div>
  );
}

/** ร่างออเดอร์จากการคุยจริงบนเพจทดสอบ: แอดมินตรวจสลิป/ยอดเอง ยังไม่ใช่ออเดอร์ในระบบ */
function TestPageDrafts() {
  const { data, isLoading, isError, refetch } = useQuery<AssistantDraft[]>({ queryKey: ['assistant-drafts'], queryFn: PreviewApi.drafts, refetchInterval: 30000 });
  return (
    <div className="border border-border rounded-lg p-3 space-y-2">
      <h3 className="font-semibold text-sm">🧾 ร่างออเดอร์และลูกค้ารอแอดมินจากเพจทดสอบ (ยังไม่บันทึกเป็นออเดอร์)</h3>
      {isLoading && <p className="text-xs text-muted-foreground">กำลังโหลด…</p>}
      {isError && <p className="text-xs text-red-700">โหลดไม่ได้ (ต้องเป็นสิทธิ์ admin)</p>}
      {data?.length === 0 && <p className="text-xs text-muted-foreground">ยังไม่มีรายการ</p>}
      {data?.map((d, i) => (
        <div key={i} className="text-xs rounded border border-border p-2 whitespace-pre-wrap">
          <div className="font-medium">{d.stage === 'handoff' ? '🙋 รอแอดมิน · ' : ''}{d.pageName} · {d.brand} · {d.updatedAt}</div>
          {d.adminNote && <div className="text-amber-700 font-medium">⚠️ {d.adminNote}</div>}
          {(d.stage === 'handoff' || d.adminNote) && <button className="my-1 underline" onClick={() => { void PreviewApi.release({ pageId: d.pageId, brand: d.brand, customerId: d.customerId }).then(() => refetch()); }}>{d.stage === 'handoff' ? 'ให้บอทกลับมาตอบต่อ' : 'รับทราบแล้ว'}</button>}
          {d.items.map((it, j) => <div key={j}>• {it.product} {it.color} {it.size} × {it.qty}</div>)}
          <div>ปัก: {d.embroidery === null ? 'ยังไม่ได้ตอบ' : d.embroidery || 'ไม่ปัก'}</div>
          <div>จัดส่ง: {d.shipping ?? '-'}</div>
          {d.total != null && <div className="font-medium">ยอดที่ต้องโอนตอนนี้: {(d.total / 100).toLocaleString('th-TH')} บาท</div>}
          {d.slip ? (
            <div className={d.slip.status === 'verified' && d.slip.expected === d.total ? 'text-green-700' : 'text-amber-700'}>
              {d.slip.status === 'verified' ? '✅ สลิปผ่านการตรวจเบื้องต้น' : d.slip.status === 'mismatch' ? '❌ สลิปไม่ตรง' : '🔎 อ่านสลิปไม่ครบ ต้องดูเอง'}
              {d.slip.amount != null && <span> · ยอดในสลิป {d.slip.amount.toLocaleString('th-TH')} บาท</span>}
              {d.slip.expected != null && d.total != null && d.slip.expected !== d.total && <span> · ⚠️ รายการเปลี่ยนหลังส่งสลิป (ตอนส่งยอด {(d.slip.expected / 100).toLocaleString('th-TH')})</span>}
              {d.slip.reasons.length > 0 && <span> · {d.slip.reasons.join(' · ')}</span>}
            </div>
          ) : <div className={d.slipSeen ? '' : 'text-amber-700'}>{d.slipSeen ? 'มีรูปที่ลูกค้าส่ง (ยังไม่ได้ตรวจยอด)' : 'ยังไม่มีรูปสลิป'}</div>}
          {d.readyToOrder && <OrderButtons d={d} onDone={() => { void refetch(); }} />}
        </div>
      ))}
    </div>
  );
}

// Keep old ReplyView export-compatible (unused now)
function ReplyView({ data: _data }: { data: PreviewResult }) {
  void _data;
  return null;
}
void ReplyView;

// ============================================================
// Calc tester — pricing engine playground
// ============================================================
function CalcTester() {
  const [brand, setBrand] = useState<Brand>('scrub');
  const [customerType, setCustomerType] = useState<CustomerType>('new');
  const [requireForm, setRequireForm] = useState(false);
  const [items, setItems] = useState<Array<{ itemKey: string; qty: number }>>([
    { itemKey: 'scrub_bundle_2pc', qty: 1 },
  ]);

  const pricing = useQuery({
    queryKey: ['pricing', brand],
    queryFn: () => PricingApi.list(brand),
  });

  const mut = useMutation({
    mutationFn: () => CalcApi.run({ brand, items, customerType, requireForm }),
  });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="space-y-3 border border-border rounded-lg p-4">
        <h3 className="font-semibold text-sm">Input</h3>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground">brand</label>
            <select
              className="w-full border border-input rounded-md h-9 px-3 text-sm"
              value={brand}
              onChange={(e) => setBrand(e.target.value as Brand)}
            >
              <option value="scrub">Cher Scrub</option>
              <option value="bow">Cherlyn</option>
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">customerType</label>
            <select
              className="w-full border border-input rounded-md h-9 px-3 text-sm"
              value={customerType}
              onChange={(e) => setCustomerType(e.target.value as CustomerType)}
            >
              <option value="new">ลูกค้าใหม่</option>
              <option value="returning">ลูกค้าเก่า</option>
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={requireForm}
            onChange={(e) => setRequireForm(e.target.checked)}
          />
          กรอกแบบสอบถามพัฒนาสินค้าแล้ว (requireForm)
        </label>

        <div>
          <label className="text-xs text-muted-foreground">items</label>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="flex gap-2">
                <select
                  className="flex-1 border border-input rounded-md h-9 px-3 text-sm"
                  value={it.itemKey}
                  onChange={(e) => {
                    const newItems = [...items];
                    newItems[i] = { ...it, itemKey: e.target.value };
                    setItems(newItems);
                  }}
                >
                  <option value="">-- เลือก --</option>
                  {pricing.data?.map((p) => (
                    <option key={p.itemKey} value={p.itemKey}>
                      {p.label} ({satangToBaht(p.price)} ฿)
                    </option>
                  ))}
                </select>
                <Input
                  type="number"
                  min={1}
                  className="w-20"
                  value={it.qty}
                  onChange={(e) => {
                    const newItems = [...items];
                    newItems[i] = { ...it, qty: Number(e.target.value) || 1 };
                    setItems(newItems);
                  }}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                >
                  ×
                </Button>
              </div>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setItems([...items, { itemKey: '', qty: 1 }])}
            >
              + เพิ่ม item
            </Button>
          </div>
        </div>

        <Button
          onClick={() => mut.mutate()}
          disabled={mut.isPending || items.some((i) => !i.itemKey)}
          className="w-full"
        >
          <Calculator size={14} className="mr-1" />
          {mut.isPending ? 'กำลังคำนวณ…' : 'คำนวณ'}
        </Button>
      </div>

      <div className="border border-border rounded-lg p-4 space-y-3">
        <h3 className="font-semibold text-sm">Result</h3>
        {mut.error && (
          <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
            {String(mut.error)}
          </div>
        )}
        {mut.data ? <CalcResultView data={mut.data} /> : (
          <div className="text-sm text-muted-foreground">
            กรอก items + กด "คำนวณ" เพื่อดูผล
          </div>
        )}
      </div>
    </div>
  );
}

function CalcResultView({ data }: { data: CalcResult }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-muted/40 p-2 rounded">
          <div className="text-xs text-muted-foreground">Subtotal</div>
          <div className="text-lg font-mono">{satangToBaht(data.subtotal)}</div>
        </div>
        <div className="bg-orange-50 p-2 rounded">
          <div className="text-xs text-muted-foreground">Discount</div>
          <div className="text-lg font-mono text-orange-700">
            −{satangToBaht(data.discount)}
          </div>
        </div>
        <div className="bg-green-50 p-2 rounded">
          <div className="text-xs text-muted-foreground">Total</div>
          <div className="text-lg font-mono font-bold text-green-700">
            {satangToBaht(data.total)} ฿
          </div>
        </div>
      </div>

      {data.appliedPromo && (
        <div className="bg-primary/5 border border-primary/20 p-2 rounded text-sm">
          <div className="font-semibold">โปรที่ใช้: {data.appliedPromo.name}</div>
          <div className="text-xs text-muted-foreground font-mono">
            {data.appliedPromo.ruleKey}
          </div>
        </div>
      )}

      <div className="border border-border rounded overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="px-2 py-1">item</th>
              <th className="px-2 py-1 text-right">qty</th>
              <th className="px-2 py-1 text-right">unit</th>
              <th className="px-2 py-1 text-right">total</th>
            </tr>
          </thead>
          <tbody>
            {data.breakdown.map((b, i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-2 py-1">{b.label}</td>
                <td className="px-2 py-1 text-right">{b.qty}</td>
                <td className="px-2 py-1 text-right font-mono">
                  {satangToBaht(b.unitPrice)}
                  {b.unitPrice !== b.listPrice && (
                    <span className="text-muted-foreground line-through ml-1">
                      {satangToBaht(b.listPrice)}
                    </span>
                  )}
                </td>
                <td className="px-2 py-1 text-right font-mono">
                  {satangToBaht(b.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.eligiblePromos.length > 1 && (
        <div className="text-xs text-muted-foreground">
          <div className="mb-1">โปรอื่นที่เข้าเงื่อนไข:</div>
          <ul className="space-y-0.5 ml-4">
            {data.eligiblePromos.slice(1).map((p) => (
              <li key={p.ruleKey}>
                • {p.name} → {satangToBaht(p.computedTotal)} ฿
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** แอดมินยืนยันรับเงินแล้วสร้างออเดอร์จริง: ส่งยอดและ version ที่เห็นไปให้ server เทียบ (รายการเปลี่ยน = ปฏิเสธ) */
function OrderButtons({ d, onDone }: { d: AssistantDraft; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  if (d.total == null) return <div className="text-amber-700">คิดยอดไม่ได้ ยังสร้างออเดอร์ไม่ได้</div>;
  const go = async () => {
    if (busy) return;
    const baht = (d.total! / 100).toLocaleString('th-TH');
    if (!window.confirm(`ยืนยันว่าได้รับเงิน ${baht} บาทเข้าบัญชีร้านแล้ว และสร้างออเดอร์? ลูกค้าจะได้รับข้อความยืนยันออเดอร์`)) return;
    setBusy(true); setMsg(null);
    try {
      const r = await PreviewApi.createOrder({ pageId: d.pageId, brand: d.brand, customerId: d.customerId, seenVersion: d.version, seenTotal: d.total! });
      setMsg(`สร้างออเดอร์ ${r.orderNo} แล้ว · ${(r.total / 100).toLocaleString('th-TH')} บาท${r.notified ? ' · แจ้งลูกค้าแล้ว' : ' · แจ้งลูกค้าไม่สำเร็จ แจ้งเองใน Messenger'}`);
      onDone();
    } catch (e) {
      const t = e instanceof Error ? e.message : String(e);
      setMsg(/total_changed|conversation_changed|not_ready/.test(t) ? 'รายการหรือยอดเปลี่ยนแล้ว กดโหลดใหม่แล้วตรวจอีกครั้ง' : `สร้างไม่ได้: ${t}`);
      onDone();
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 pt-1">
      <button disabled={busy} className="rounded bg-green-700 text-white px-2 py-1 disabled:opacity-50" onClick={() => void go()}>✅ ได้รับเงินแล้ว · สร้างออเดอร์</button>
      {msg && <span className="text-foreground">{msg}</span>}
    </div>
  );
}
